import { isOfflineFlagEnabled } from '@/configs/offlineFeatureFlags.js';
import offlineDB from '@/indexedDB/offlineDB.js';
import { handleSyncEvent } from '@/services/offlineRealtimeService.js';
import { nextAttemptAt, runOfflineListSync } from '@/services/offlineSyncService.js';
import { canMutateOnline } from '@/services/onlineCheckService.js';
import { recordSyncEvent } from '@/services/syncTelemetry.js';
import apiClient from '@/utils/axios.js';

/**
 * Replayable change feed client (spike — see docs/DEXIE_OFFLINE.md).
 *
 * The session holds one cursor into the server's ordered log. Live, Reverb
 * delivers the same changes; after any gap — reconnect, login, a slept tab —
 * they are replayed from `/api/sync/changes` and applied through the *same*
 * function the socket listener uses. That is what makes the socket an
 * optimization rather than a second convergence path.
 *
 * A feed row names an entity, never its contents. So an `upsert` becomes a
 * local `invalidate`, which the realtime layer already knows how to answer: it
 * schedules the table's own authorized refresh, debounced, and that refresh is
 * now an incremental fetch. Fifty changed sales cost one delta, not fifty reads.
 */

/** Single global cursor — per-table cursors can skew and reorder related rows. */
const CURSOR_KEY = '__change_feed__:global';
const PAGE_LIMIT = 200;
/** Bounds one run; a client further behind than this finishes on the next pass. */
const MAX_BATCHES = 20;

let inflight = null;

export async function getChangeFeedCursor() {
    const row = await offlineDB.offline_sync_meta?.get(CURSOR_KEY);
    const cursor = Number(row?.cursor);
    return Number.isSafeInteger(cursor) && cursor > 0 ? cursor : 0;
}

/**
 * The access revision this cursor was earned under.
 *
 * Sent back with every poll: if the server's revision has moved, the reader's
 * own permissions or branch access changed, no log entry retracts what they can
 * no longer see, and the cursor is void.
 */
export async function getSubscriptionRevision() {
    const row = await offlineDB.offline_sync_meta?.get(CURSOR_KEY);
    const revision = Number(row?.revision);
    return Number.isSafeInteger(revision) && revision >= 0 ? revision : 0;
}

export async function setChangeFeedCursor(cursor, revision = null) {
    const value = Number(cursor);
    if (!Number.isSafeInteger(value) || value < 0) return;

    const existing = (await offlineDB.offline_sync_meta?.get(CURSOR_KEY)) || {};
    const nextRevision = Number(revision);

    await offlineDB.offline_sync_meta.put({
        ...existing,
        key: CURSOR_KEY,
        table: '__change_feed__',
        scope: 'global',
        cursor: value,
        revision: Number.isSafeInteger(nextRevision) && nextRevision >= 0 ? nextRevision : (existing.revision ?? 0),
        last_success_at: new Date().toISOString(),
        // The cursor moved, so the feed is answering. Anything the ladder below
        // was holding against it is settled.
        consecutive_failures: 0,
        next_attempt_at: null,
    });
}

/**
 * Put the feed into the same retry ladder its list siblings already walk.
 *
 * It had none. A poll that 503'd was simply re-attempted by whatever fired next
 * — a login, a reconnect, a tab resume, and every realtime event through
 * `scheduleFeedCatchUp` — so one failing endpoint became a request per trigger
 * for as long as it stayed down. Worse, `convergeOfflineCaches` reads a failed
 * replay as grounds to run the thirteen-table watermark scan *past its own
 * floor*, so each of those failures dragged the expensive fallback along behind
 * it. Sharing `nextAttemptAt` keeps one ladder shape across the whole service.
 */
async function recordFeedFailure(error) {
    try {
        if (!offlineDB.offline_sync_meta) return;
        const existing = (await offlineDB.offline_sync_meta.get(CURSOR_KEY)) || {};
        const failures = Number(existing.consecutive_failures ?? 0) + 1;
        await offlineDB.offline_sync_meta.put({
            ...existing,
            key: CURSOR_KEY,
            table: '__change_feed__',
            scope: 'global',
            last_error: String(error?.message || error),
            consecutive_failures: failures,
            next_attempt_at: nextAttemptAt(failures),
        });
    } catch (e) {
        console.warn('[changeFeed] could not record failure', e);
    }
}

/**
 * A feed row carries identifiers only, so it maps onto the realtime envelope
 * the apply layer already validates.
 */
function toSyncEvent(change) {
    const table = change?.entity;
    const id = change?.id ?? null;

    if (change?.op === 'delete' && id != null) {
        return { schema_version: 1, table, action: 'delete', payload: { id }, entity_id: id };
    }

    // Everything else is "this went stale, re-read it yourself". The routing
    // hints ride along because branch_stocks needs to know *which* branch.
    return {
        schema_version: 1,
        table,
        action: 'invalidate',
        payload: change?.routing ?? null,
        entity_id: id,
    };
}

function unwrap(response) {
    const body = response?.data;
    return body?.data && !Array.isArray(body.data) ? body.data : body;
}

export async function runChangeFeedSync({ reason = 'manual' } = {}) {
    if (!isOfflineFlagEnabled('changeFeedSync')) {
        return { ok: false, reason: 'flag_disabled' };
    }
    if (!canMutateOnline()) {
        return { ok: false, reason: 'offline' };
    }
    // Login, reconnect and a visibility resume can all fire at once; one replay
    // is enough and a second would only re-apply the same cursor range.
    if (inflight) return inflight;

    inflight = (async () => {
        const meta = (await offlineDB.offline_sync_meta?.get(CURSOR_KEY)) || {};
        const retryAt = meta.next_attempt_at ? Date.parse(meta.next_attempt_at) : NaN;
        if (!Number.isNaN(retryAt) && retryAt > Date.now()) {
            // Distinct from a failure on purpose: convergeOfflineCaches escalates
            // to the watermark scan when a replay *fails*, and a run we chose not
            // to make is not evidence that anything is wrong right now.
            return { ok: false, reason: 'backoff', retryAt: meta.next_attempt_at };
        }

        let cursor = await getChangeFeedCursor();
        const revision = await getSubscriptionRevision();
        let applied = 0;

        try {
            for (let batch = 0; batch < MAX_BATCHES; batch += 1) {
                const response = await apiClient.get('sync/changes', {
                    params: { since: cursor, revision, limit: PAGE_LIMIT },
                    offlinePolicy: 'read',
                    skipProgress: true,
                });

                const body = unwrap(response) || {};

                // Three ways a cursor becomes unusable: the session never had
                // one, it predates what the server retains, or the reader's own
                // access changed underneath it. None can be repaired by replay.
                if (body.reseed) {
                    const seeded = await runOfflineListSync({ reason: `change_feed_reseed:${reason}`, mode: 'full' });
                    if (!seeded?.ok) {
                        // A reseed that cannot complete leaves the cursor unusable,
                        // so every later poll would ask for the same reseed again.
                        await recordFeedFailure(new Error('reseed failed'));
                        return { ok: false, reason: 'reseed_failed', cursor };
                    }
                    await setChangeFeedCursor(body.cursor ?? 0, body.revision);
                    recordSyncEvent('reseed', { reason });
                    return { ok: true, reseeded: true, cursor: body.cursor ?? 0, applied: 0 };
                }

                for (const change of body.changes ?? []) {
                    await handleSyncEvent(toSyncEvent(change));
                    applied += 1;
                }

                // Advanced only after the batch applied. At-least-once replay is
                // fine because every op is an idempotent re-read; skipping one
                // is not, so the cursor must never run ahead of the apply.
                cursor = Number(body.cursor) || cursor;
                await setChangeFeedCursor(cursor, body.revision);

                if (!body.hasMore) {
                    recordSyncEvent('replay', { reason, applied });
                    return { ok: true, cursor, applied };
                }
            }

            recordSyncEvent('replay_truncated', { reason, applied });
            return { ok: true, cursor, applied, truncated: true };
        } catch (error) {
            console.warn('[changeFeed] replay failed', reason, error);
            recordSyncEvent('replay_failed', { reason, message: String(error?.message || error) });
            await recordFeedFailure(error);
            return { ok: false, reason: 'error', error, cursor, applied };
        }
    })().finally(() => {
        inflight = null;
    });

    return inflight;
}

export default { runChangeFeedSync, getChangeFeedCursor, setChangeFeedCursor };
